import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavStack } from '@/hooks/useNavLayer';
import { getReadingAccessSnapshot, type ReadingAccessSnapshot } from '@/lib/reading-access';
import type { OfflineReopenSnapshot } from '@/lib/offline-reopen';
import { readRememberedSession, sdkSessionStorageKey } from '@/lib/offline-reopen-session';
import { BIBLE_ENABLED } from '@/lib/feature-flags';

// Presentation hint only: no owner, route, chapter, scroll, or admission grant.
// The destination always comes from the separately fenced local snapshot.
const LAST_SURFACE_KEY = 'adia-last-surface';
const COLD_RESUME_TIMEOUT_MS = 5_000;

function lastSurfaceWasBible(): boolean {
  try { return localStorage.getItem(LAST_SURFACE_KEY) === 'bible'; }
  catch { return false; }
}

function hasOpenInteraction(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>(
    '[data-offline-reopen] :is(dialog[open], [role="dialog"], [role="alertdialog"], input:not([type="hidden"]), textarea, [contenteditable="true"])',
  )).some((element) => element.getClientRects().length > 0);
}

export function getRememberedOfflineOwnerId(): string | null {
  const projectUrl = import.meta.env?.VITE_SUPABASE_URL;
  if (!projectUrl) return null;
  try {
    const read = readRememberedSession(() => localStorage.getItem(sdkSessionStorageKey(projectUrl)), projectUrl);
    return read.kind === 'present' ? read.session.ownerId : null;
  } catch { return null; }
}

/** Sheet PUSH/POP entries are the same visit, not a new base navigation. */
export function useBaseRouteVisitKey(): string {
  const location = useLocation();
  const path = location.pathname + location.search + location.hash;
  const storedDepth = (location.state as { navDepth?: number } | null)?.navDepth;
  const depth = typeof storedDepth === 'number' && Number.isInteger(storedDepth) && storedDepth > 0
    ? storedDepth : 0;
  const [visit, setVisit] = useState({ historyKey: location.key, generation: 0, path, depth });
  if (visit.historyKey !== location.key) {
    const sheetNavigation = visit.path === path && (visit.depth > 0 || depth > 0);
    // History POP may return to an old location.key. A real route departure
    // consumed that visit; never resurrect its held handoff decision on Back.
    const next = { historyKey: location.key,
      generation: visit.generation + (sheetNavigation ? 0 : 1), path, depth };
    setVisit(next);
    return String(next.generation);
  }
  return String(visit.generation);
}

/** Decide once at admission; blur/sheet close never dismiss a held visit. */
export function useVerifiedHomeHandoff(retaining: boolean, admitted: boolean, visitKey: string): boolean {
  const location = useLocation();
  const nav = useNavStack();
  const [decision, setDecision] = useState<{ key: string; handoff: boolean } | null>(null);
  const ready = retaining && admitted && location.pathname === '/';
  useLayoutEffect(() => {
    if (!ready || decision?.key === visitKey) return;
    let disposed = false;
    // Inspect committed sheet/input DOM after all layout effects. Never close
    // an interaction to make a Home handoff possible, and never poll for blur.
    queueMicrotask(() => {
      if (!disposed) setDecision({ key: visitKey,
        handoff: nav.length() === 0 && !hasOpenInteraction() });
    });
    return () => { disposed = true; };
  }, [ready, decision?.key, visitKey, nav]);
  return ready && decision?.key === visitKey && decision.handoff;
}

/** Restore only the first offline '/' launch; later reconnects cannot redirect. */
export function useOfflineBibleResume(offline: boolean, reopen: OfflineReopenSnapshot,
  access: ReadingAccessSnapshot, signingIn: boolean): boolean {
  const location = useLocation();
  const navigate = useNavigate();
  const visitKey = useBaseRouteVisitKey();
  const nav = useNavStack();
  const [launch] = useState(() => ({
    key: visitKey,
    deadline: performance.now() + COLD_RESUME_TIMEOUT_MS,
    offline: navigator.onLine === false,
    ownerId: getRememberedOfflineOwnerId(),
    // A candidate may wait for the cold refresh's transport decision. Merely
    // running that check never permits a chapter redirect on an online launch.
    resume: BIBLE_ENABLED && location.pathname === '/' && !location.search && !location.hash &&
      (navigator.onLine === false || reopen.phase === 'checking' || reopen.presentationFailure !== undefined) &&
      lastSurfaceWasBible(),
  }));
  const [pending, setPending] = useState(launch.resume);

  useEffect(() => {
    if (!pending) return;
    let disposed = false;
    const finish = (destination?: string) => {
      if (disposed) return;
      if (destination) navigate(destination, { replace: true });
      setPending(false);
    };
    // A passive microtask runs after BrowserRouter has subscribed. In
    // particular, never perform a cold replace from a mount layout effect.
    queueMicrotask(() => {
      if (disposed) return;
      const leftLaunch = location.pathname !== '/' || Boolean(location.search || location.hash) ||
        visitKey !== launch.key;
      if (leftLaunch || signingIn || nav.length() > 0 || (!launch.offline && reopen.phase === 'pass')) {
        finish();
        return;
      }
      const failedConnection = reopen.presentationFailure !== undefined;
      if (!launch.offline && !failedConnection) {
        if (reopen.phase !== 'checking') finish();
        return;
      }
      const current = getReadingAccessSnapshot();
      const local = current.local;
      const lastRead = local?.lastRead;
      const ownerId = getRememberedOfflineOwnerId();
      if (ownerId && ownerId === launch.ownerId && local?.ownerId === ownerId && lastRead?.ownerId === ownerId) {
        finish(`/bible/${lastRead.bookSlug}/${lastRead.chapter}`);
      } else if (reopen.phase !== 'checking') {
        // Missing/unbound/other-account history is not inferred or repaired.
        finish();
      }
    });
    // A contended/private-cache failure cannot leave the public shell blank.
    // A later snapshot or reconnect never revives a consumed launch request.
    const timer = window.setTimeout(() => finish(), Math.max(0, launch.deadline - performance.now()));
    return () => { disposed = true; window.clearTimeout(timer); };
  }, [pending, launch.key, launch.deadline, launch.offline, launch.ownerId, visitKey,
    location.pathname, location.search, location.hash, navigate, nav, offline,
    reopen.phase, reopen.presentationFailure, access.epoch, signingIn]);

  useEffect(() => {
    // Do not overwrite the Bible hint with the manifest's initial '/' before
    // its restore decision. This is global navigation chrome, never owner data.
    if (pending) return;
    try {
      localStorage.setItem(LAST_SURFACE_KEY,
        /^\/bible\/[^/]+\/\d+\/?$/.test(location.pathname) &&
          !/^\/bible\/(bookmarks|overview)(\/|$)/.test(location.pathname) ? 'bible' : 'home');
    } catch { /* A missing presentation hint simply keeps the next launch Home. */ }
  }, [pending, location.pathname]);

  return pending && location.pathname === '/';
}
